import { createContext, useContext, type ReactNode } from 'react'

/** Mirrors portal session surface area (Auth0 or dev bypass). */
export interface PortalAuthState {
  isLoading: boolean
  isAuthenticated: boolean
  loginWithRedirect: (opts?: { signup?: boolean }) => Promise<void>
  /**
   * Start Auth0 step-up MFA (`acr_values` multi-factor) using the existing SSO session
   * so Universal Login can go straight to the SMS code screen.
   * Prefer `returnTo: '/dashboard'`. Only pass `forceLogin` as a last resort if SSO
   * skips the SMS challenge (it forces `prompt=login` and a password re-entry).
   */
  loginWithMfaEnrollment: (opts?: {
    returnTo?: string
    forceLogin?: boolean
  }) => Promise<void>
  logout: (opts?: { returnTo?: string }) => Promise<void>
  /** Set when Auth0 returns an error from the OAuth callback (e.g. unverified email). */
  authError?: unknown
}

export const PortalAuthContext = createContext<PortalAuthState | null>(null)

/** Returns an access token for the API, or null if none can be obtained. */
export const AccessTokenContext = createContext<(() => Promise<string | null>) | undefined>(undefined)

const fallbackGetAccessToken = async (): Promise<string | null> => null

export function usePortalAuth(): PortalAuthState {
  const ctx = useContext(PortalAuthContext)
  if (!ctx) {
    throw new Error('usePortalAuth must be used within AuthProvider')
  }
  return ctx
}

/** Returns the access-token getter from AuthProvider (or a no-op in tests). */
export const IdTokenClaimsContext = createContext<
  (() => Promise<Record<string, unknown> | undefined>) | undefined
>(undefined)

const fallbackGetIdTokenClaims = async (): Promise<Record<string, unknown> | undefined> =>
  undefined

export function useAccessToken(): () => Promise<string | null> {
  const getter = useContext(AccessTokenContext)
  return getter ?? fallbackGetAccessToken
}

/** Returns ID token claims for MFA gate checks (Auth0 only; no-op in tests / dev bypass). */
export const PortalIdTokenContext = createContext<
  (() => Promise<string | null>) | undefined
>(undefined)

const fallbackGetPortalIdToken = async (): Promise<string | null> => null

export function useIdTokenClaims(): () => Promise<Record<string, unknown> | undefined> {
  const getter = useContext(IdTokenClaimsContext)
  return getter ?? fallbackGetIdTokenClaims
}

/** Returns the raw Auth0 ID token for API MFA verification (`X-Portal-Id-Token`). */
export function usePortalIdToken(): () => Promise<string | null> {
  const getter = useContext(PortalIdTokenContext)
  return getter ?? fallbackGetPortalIdToken
}

/** Dev / tests: wrap children with stable portal auth + token getter. */
export function StaticPortalAuthProviders({
  value,
  getAccessToken,
  getIdTokenClaims,
  getPortalIdToken,
  children,
}: {
  value: PortalAuthState
  getAccessToken: () => Promise<string | null>
  getIdTokenClaims?: () => Promise<Record<string, unknown> | undefined>
  getPortalIdToken?: () => Promise<string | null>
  children: ReactNode
}) {
  const idClaimsGetter = getIdTokenClaims ?? fallbackGetIdTokenClaims
  const idTokenGetter = getPortalIdToken ?? fallbackGetPortalIdToken
  return (
    <PortalAuthContext.Provider value={value}>
      <AccessTokenContext.Provider value={getAccessToken}>
        <IdTokenClaimsContext.Provider value={idClaimsGetter}>
          <PortalIdTokenContext.Provider value={idTokenGetter}>
            {children}
          </PortalIdTokenContext.Provider>
        </IdTokenClaimsContext.Provider>
      </AccessTokenContext.Provider>
    </PortalAuthContext.Provider>
  )
}
